import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface LandingSection {
  id: string;
  slug: string;
  name: string;
  is_visible: boolean;
  display_order: number;
  visible_from: string | null;
  visible_until: string | null;
  content: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface UseLandingSectionsResult {
  sections: LandingSection[];
  isLoading: boolean;
  error: string | null;
  getSectionContent: <T>(slug: string) => T | null;
  isSectionVisible: (slug: string) => boolean;
  refetch: () => Promise<void>;
}

export function useLandingSections(includeHidden = false): UseLandingSectionsResult {
  const [sections, setSections] = useState<LandingSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use supabase.functions.invoke instead of raw fetch
      const { data, error: fnError } = await supabase.functions.invoke('landing-sections-get', {
        body: null,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Add query parameter for admin mode by making a separate call
      if (includeHidden) {
        const response = await fetch(
          `https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/landing-sections-get?all=true`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch sections');
        }
        const result = await response.json();
        setSections(result.sections || []);
      } else {
        if (fnError) {
          throw fnError;
        }
        setSections(data?.sections || []);
      }
    } catch (err) {
      console.error('Error fetching landing sections:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [includeHidden]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const getSectionContent = useCallback(<T,>(slug: string): T | null => {
    const section = sections.find(s => s.slug === slug);
    if (!section) return null;
    return section.content as T;
  }, [sections]);

  const isSectionVisible = useCallback((slug: string): boolean => {
    const section = sections.find(s => s.slug === slug);
    if (!section) return false;
    
    // Check visibility flag
    if (!section.is_visible) return false;

    // Check date ranges
    const today = new Date().toISOString().split('T')[0];
    if (section.visible_from && section.visible_from > today) return false;
    if (section.visible_until && section.visible_until < today) return false;

    return true;
  }, [sections]);

  return {
    sections,
    isLoading,
    error,
    getSectionContent,
    isSectionVisible,
    refetch: fetchSections,
  };
}

// Admin hook with update functionality
export function useLandingSectionsAdmin() {
  const { sections, isLoading, error, refetch } = useLandingSections(true);

  const updateSection = useCallback(async (
    id: string, 
    updates: Partial<Pick<LandingSection, 'is_visible' | 'display_order' | 'visible_from' | 'visible_until' | 'content' | 'name'>>
  ) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/landing-sections-update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ id, updates }),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to update section');
    }

    await refetch();
  }, [refetch]);

  const reorderSections = useCallback(async (orderedIds: string[]) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/landing-sections-reorder`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ orderedIds }),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to reorder sections');
    }

    await refetch();
  }, [refetch]);

  return {
    sections,
    isLoading,
    error,
    refetch,
    updateSection,
    reorderSections,
  };
}
